import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dataService, Notification } from '../services/dataService';
import { useNotificationPopup } from '../contexts/NotificationContext';
import { notificationService } from '../services/notificationService';

export const useNotifications = () => {
  const { tenantData } = useAuth();
  const { showNotification } = useNotificationPopup();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantData?.id) return;

    const unsubscribe = dataService.subscribeToNotifications(tenantData.id, (newNotifications) => {
      // Check if there are new unread notifications
      const newUnreadNotifications = newNotifications.filter(n => !n.isRead);
      
      // Show popup for the most recent unread notification
      if (newUnreadNotifications.length > 0) {
        const mostRecent = newUnreadNotifications[0];
        showNotification(mostRecent);
        
        // Also show Android notification if available
        if (mostRecent.type === 'support' && mostRecent.metadata?.adminName) {
          notificationService.showSupportNotification(
            mostRecent.metadata.adminName,
            mostRecent.metadata.messagePreview || ''
          );
        } else if (mostRecent.type === 'order' && mostRecent.metadata?.orderId) {
          notificationService.showOrderNotification(
            mostRecent.metadata.orderId,
            mostRecent.metadata.status || 'updated'
          );
        } else if (mostRecent.type === 'payment' && mostRecent.metadata?.amount) {
          notificationService.showPaymentNotification(
            mostRecent.metadata.amount,
            mostRecent.metadata.method || 'payment'
          );
        } else {
          // Show generic notification
          notificationService.showLocalNotification(mostRecent);
        }
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadNotifications.length);
    });

    return unsubscribe;
  }, [tenantData?.id, showNotification]);

  const markAsRead = async (notificationId: string) => {
    if (!tenantData?.id) return;
    
    try {
      await dataService.markNotificationAsRead(tenantData.id, notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!tenantData?.id) return;
    
    try {
      await dataService.markAllNotificationsAsRead(tenantData.id);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!tenantData?.id) return;
    
    try {
      await dataService.deleteNotification(tenantData.id, notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const deleteAllNotifications = async () => {
    if (!tenantData?.id) return;
    
    try {
      await dataService.deleteAllNotifications(tenantData.id);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  const createNotification = async (
    title: string,
    message: string,
    type: Notification['type'] = 'info',
    priority: Notification['priority'] = 'medium',
    metadata?: Notification['metadata']
  ) => {
    if (!tenantData?.id) return;
    
    try {
      await dataService.createSystemNotification(tenantData.id, title, message, type, priority, metadata);
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    createNotification
  };
}; 